import React, { useEffect, useState } from "react";
import { Box, Typography, Button, Avatar, Chip, IconButton, Dialog, DialogContent, DialogActions, TextField, Select, MenuItem, FormControl, InputLabel, Tooltip, CircularProgress } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import GroupIcon from "@mui/icons-material/Group";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import { useNavigate } from "react-router-dom";
import client from "../api/client";

const API = (process.env.REACT_APP_AUTH_API || "/api/auth").replace("/api/auth", "/api");

const ROLE_CONFIG = {
  agent:      { color: "#6366f1", bg: "#eef2ff", label: "Agent" },
  supervisor: { color: "#f59e0b", bg: "#fffbeb", label: "Supervisor" },
};

export default function TeamManagement() {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ fullName: "", email: "", password: "", role: "agent" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const navigate = useNavigate();

  const stored = JSON.parse(localStorage.getItem("user") || "{}");
  const user = stored?.user || stored;
  const limit = user?.planLimits?.liveAgents ?? 0;

  const fetchAgents = async () => {
    try {
      const res = await client.get(`${API}/agents`);
      setAgents(res.data);
    } catch {}
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAgents(); }, []);

  const handleAdd = async () => {
    if (!form.fullName || !form.email || !form.password) return setError("All fields required");
    setSaving(true); setError("");
    try {
      await client.post(`${API}/agents`, form);
      setOpen(false);
      setForm({ fullName: "", email: "", password: "", role: "agent" });
      fetchAgents();
    } catch (err) {
      const data = err.response?.data;
      if (data?.upgrade) { setOpen(false); setUpgradeOpen(true); }
      else setError(data?.message || "Failed to add agent");
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    try {
      await client.delete(`${API}/agents/${id}`);
      setAgents(prev => prev.filter(a => a._id !== id));
    } catch {}
  };

  const canAdd = limit === -1 || agents.length < limit;
  const onlineCount = agents.filter(a => a.agentStatus === "online").length;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 4 }}>
        <Box>
          <Typography fontWeight={800} fontSize={24} color="#111827" letterSpacing="-0.3px">Team Management</Typography>
          <Typography fontSize={14} color="#6b7280" mt={0.5}>
            Manage your live chat agents and supervisors
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />}
          onClick={() => canAdd ? setOpen(true) : setUpgradeOpen(true)}
          sx={{ textTransform: "none", borderRadius: 2.5, fontWeight: 700, px: 2.5, py: 1.2,
            background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
            boxShadow: "0 4px 14px rgba(99,102,241,0.35)",
            "&:hover": { background: "linear-gradient(135deg, #4f46e5, #7c3aed)" } }}>
          Add Agent
        </Button>
      </Box>

      {/* Stats row */}
      <Box sx={{ display: "flex", gap: 2, mb: 4 }}>
        {[
          { label: "Total Agents", value: agents.length, color: "#6366f1", bg: "#eef2ff" },
          { label: "Online Now", value: onlineCount, color: "#10b981", bg: "#f0fdf4" },
          { label: "Agent Limit", value: limit === -1 ? "∞" : limit, color: "#f59e0b", bg: "#fffbeb" },
        ].map(s => (
          <Box key={s.label} sx={{ flex: 1, bgcolor: s.bg, borderRadius: 2.5, p: 2, border: `1px solid ${s.color}20` }}>
            <Typography fontSize={28} fontWeight={800} color={s.color}>{s.value}</Typography>
            <Typography fontSize={12} color="#6b7280" fontWeight={600}>{s.label}</Typography>
          </Box>
        ))}
      </Box>

      {/* Two column layout */}
      <Box sx={{ display: "flex", gap: 3, alignItems: "flex-start" }}>

      {/* Left — agents list */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}><CircularProgress sx={{ color: "#6366f1" }} /></Box>
      ) : agents.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 8, px: 4, bgcolor: "#fafafa", borderRadius: 3, border: "2px dashed #e5e7eb" }}>
          <Box sx={{ width: 72, height: 72, borderRadius: "20px", bgcolor: "#eef2ff", display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 2 }}>
            <GroupIcon sx={{ fontSize: 36, color: "#6366f1" }} />
          </Box>
          <Typography fontWeight={700} fontSize={16} color="#111827" mb={0.5}>No agents yet</Typography>
          <Typography fontSize={14} color="#6b7280" mb={3}>
            {limit === 0 ? "Upgrade your plan to add live chat agents" : "Add your first agent to start handling live chats"}
          </Typography>
          {limit === 0 ? (
            <Button variant="contained" onClick={() => navigate("/app/plans")}
              sx={{ textTransform: "none", borderRadius: 2, background: "linear-gradient(135deg, #6366f1, #8b5cf6)" }}>
              View Plans
            </Button>
          ) : (
            <Button variant="contained" startIcon={<PersonAddIcon />} onClick={() => setOpen(true)}
              sx={{ textTransform: "none", borderRadius: 2, background: "linear-gradient(135deg, #6366f1, #8b5cf6)" }}>
              Add First Agent
            </Button>
          )}
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {agents.map((agent, i) => {
            const rc = ROLE_CONFIG[agent.role] || ROLE_CONFIG.agent;
            const isOnline = agent.agentStatus === "online";
            return (
              <Box key={agent._id} sx={{
                display: "flex", alignItems: "center", gap: 2, px: 2.5, py: 2,
                bgcolor: "#fff", borderRadius: 2.5, border: "1px solid #f3f4f6",
                boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
                transition: "box-shadow 0.2s",
                "&:hover": { boxShadow: "0 4px 16px rgba(99,102,241,0.1)", borderColor: "#e0e7ff" },
              }}>
                {/* Avatar with status */}
                <Box sx={{ position: "relative", flexShrink: 0 }}>
                  <Avatar sx={{ width: 44, height: 44, background: `linear-gradient(135deg, ${rc.color}, ${rc.color}cc)`, fontWeight: 800, fontSize: 16, boxShadow: `0 3px 10px ${rc.color}30` }}>
                    {agent.fullName.charAt(0).toUpperCase()}
                  </Avatar>
                  <Box sx={{ position: "absolute", bottom: 1, right: 1, width: 11, height: 11, borderRadius: "50%", bgcolor: isOnline ? "#10b981" : "#d1d5db", border: "2px solid #fff" }} />
                </Box>

                {/* Info */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography fontWeight={700} fontSize={14} color="#111827">{agent.fullName}</Typography>
                    <Chip label={rc.label} size="small" sx={{ bgcolor: rc.bg, color: rc.color, fontWeight: 700, fontSize: 10, height: 20 }} />
                  </Box>
                  <Typography fontSize={12} color="#9ca3af" noWrap>{agent.email}</Typography>
                </Box>

                {/* Status */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, px: 1.5, py: 0.5, borderRadius: 2, bgcolor: isOnline ? "#f0fdf4" : "#f9fafb", border: `1px solid ${isOnline ? "#bbf7d0" : "#e5e7eb"}` }}>
                  <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: isOnline ? "#10b981" : "#d1d5db" }} />
                  <Typography fontSize={11} fontWeight={700} color={isOnline ? "#10b981" : "#9ca3af"}>
                    {isOnline ? "Online" : "Offline"}
                  </Typography>
                </Box>

                {/* Delete */}
                <Tooltip title="Remove agent">
                  <IconButton size="small" onClick={() => handleDelete(agent._id)}
                    sx={{ color: "#d1d5db", "&:hover": { color: "#ef4444", bgcolor: "#fef2f2" }, transition: "all 0.15s" }}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            );
          })}
        </Box>
      )}

      </Box>
      {/* End agents list */}

      {/* Right — Info panel */}
      <Box sx={{ width: 280, flexShrink: 0 }}>
        {/* Roles guide */}
        <Box sx={{ bgcolor: "#fff", borderRadius: 2.5, border: "1px solid #f3f4f6", p: 2.5, mb: 2 }}>
          <Typography fontWeight={700} fontSize={13} color="#111827" mb={1.5}>Role Guide</Typography>
          {[
            { role: "Agent", color: "#6366f1", bg: "#eef2ff", desc: "Handles live chat requests from visitors" },
            { role: "Supervisor", color: "#f59e0b", bg: "#fffbeb", desc: "Monitors agents and can transfer chats" },
          ].map(r => (
            <Box key={r.role} sx={{ display: "flex", gap: 1.5, mb: 1.5, "&:last-child": { mb: 0 } }}>
              <Box sx={{ width: 32, height: 32, borderRadius: "8px", bgcolor: r.bg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <Typography fontSize={11} fontWeight={800} color={r.color}>{r.role.charAt(0)}</Typography>
              </Box>
              <Box>
                <Typography fontSize={12} fontWeight={700} color={r.color}>{r.role}</Typography>
                <Typography fontSize={11} color="#9ca3af" lineHeight={1.4}>{r.desc}</Typography>
              </Box>
            </Box>
          ))}
        </Box>

        {/* Plan info */}
        <Box sx={{ bgcolor: "linear-gradient(135deg, #eef2ff, #faf5ff)", background: "linear-gradient(135deg, #eef2ff, #faf5ff)", borderRadius: 2.5, border: "1px solid #e0e7ff", p: 2.5, mb: 2 }}>
          <Typography fontWeight={700} fontSize={13} color="#111827" mb={1}>Plan Limits</Typography>
          <Box sx={{ display: "flex", justifyContent: "space-between", py: 0.8, borderBottom: "1px solid #e0e7ff" }}>
            <Typography fontSize={12} color="#6b7280">Used</Typography>
            <Typography fontSize={12} fontWeight={700} color="#6366f1">{agents.length} agents</Typography>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "space-between", py: 0.8 }}>
            <Typography fontSize={12} color="#6b7280">Limit</Typography>
            <Typography fontSize={12} fontWeight={700} color="#6366f1">{limit === -1 ? "Unlimited" : `${limit} agents`}</Typography>
          </Box>
          {limit !== -1 && limit > 0 && (
            <Box sx={{ mt: 1.5, height: 6, bgcolor: "#e0e7ff", borderRadius: 3, overflow: "hidden" }}>
              <Box sx={{ height: "100%", width: `${Math.min((agents.length / limit) * 100, 100)}%`, background: "linear-gradient(135deg, #6366f1, #8b5cf6)", borderRadius: 3, transition: "width 0.3s" }} />
            </Box>
          )}
        </Box>

        {/* Tips */}
        <Box sx={{ bgcolor: "#fff", borderRadius: 2.5, border: "1px solid #f3f4f6", p: 2.5 }}>
          <Typography fontWeight={700} fontSize={13} color="#111827" mb={1.5}>💡 Tips</Typography>
          {[
            "Agents must be online to receive auto-assigned chats",
            "Supervisors can transfer chats between agents",
            "Agents only see the Live Chat dashboard",
          ].map((tip, i) => (
            <Box key={i} sx={{ display: "flex", gap: 1, mb: 1, "&:last-child": { mb: 0 } }}>
              <Box sx={{ width: 4, height: 4, borderRadius: "50%", bgcolor: "#6366f1", mt: 0.7, flexShrink: 0 }} />
              <Typography fontSize={11} color="#6b7280" lineHeight={1.5}>{tip}</Typography>
            </Box>
          ))}
        </Box>
      </Box>
      {/* End two-column */}
      </Box>

      {/* Add Agent Dialog */}
      <Dialog open={open} onClose={() => { setOpen(false); setError(""); }} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}>
        {/* Header */}
        <Box sx={{ background: "linear-gradient(135deg, #6366f1, #8b5cf6)", px: 3, py: 2.5, display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box sx={{ width: 40, height: 40, borderRadius: "12px", bgcolor: "rgba(255,255,255,0.15)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <PersonAddIcon sx={{ color: "#fff", fontSize: 22 }} />
          </Box>
          <Box>
            <Typography fontWeight={800} fontSize={16} color="#fff">Add New Agent</Typography>
            <Typography fontSize={12} color="rgba(255,255,255,0.7)">Create a team member account</Typography>
          </Box>
        </Box>

        <DialogContent sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2 }}>
          {/* Role selector */}
          <Box sx={{ display: "flex", gap: 1 }}>
            {["agent", "supervisor"].map(r => (
              <Box key={r} onClick={() => setForm(f => ({ ...f, role: r }))}
                sx={{ flex: 1, p: 1.5, borderRadius: 2, cursor: "pointer", border: "2px solid", textAlign: "center",
                  borderColor: form.role === r ? ROLE_CONFIG[r].color : "#f3f4f6",
                  bgcolor: form.role === r ? ROLE_CONFIG[r].bg : "#fafafa",
                  transition: "all 0.15s" }}>
                <Typography fontSize={13} fontWeight={700} color={form.role === r ? ROLE_CONFIG[r].color : "#6b7280"}>
                  {ROLE_CONFIG[r].label}
                </Typography>
                <Typography fontSize={11} color="#9ca3af">
                  {r === "agent" ? "Handles chats" : "Monitors agents"}
                </Typography>
              </Box>
            ))}
          </Box>

          <TextField label="Full Name" size="small" fullWidth value={form.fullName}
            onChange={e => setForm(f => ({ ...f, fullName: e.target.value }))}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
          <TextField label="Email Address" size="small" fullWidth value={form.email}
            onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
          <TextField label="Password" type="password" size="small" fullWidth value={form.password}
            onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
            helperText="Min 8 characters"
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />

          {error && (
            <Box sx={{ p: 1.5, bgcolor: "#fef2f2", border: "1px solid #fecaca", borderRadius: 1.5 }}>
              <Typography fontSize={13} color="#ef4444">{error}</Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, pt: 0, gap: 1 }}>
          <Button onClick={() => { setOpen(false); setError(""); }} fullWidth variant="outlined"
            sx={{ textTransform: "none", borderRadius: 2, borderColor: "#e5e7eb", color: "#6b7280" }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleAdd} disabled={saving} fullWidth
            sx={{ textTransform: "none", borderRadius: 2, background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
              "&.Mui-disabled": { background: "#e0e7ff", color: "#a5b4fc" } }}>
            {saving ? "Creating..." : "Create Agent"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Upgrade Dialog */}
      <Dialog open={upgradeOpen} onClose={() => setUpgradeOpen(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}>
        <Box sx={{ background: "linear-gradient(135deg, #f59e0b, #f97316)", px: 3, py: 2.5, display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box sx={{ width: 40, height: 40, borderRadius: "12px", bgcolor: "rgba(255,255,255,0.15)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <RocketLaunchIcon sx={{ color: "#fff", fontSize: 22 }} />
          </Box>
          <Box>
            <Typography fontWeight={800} fontSize={16} color="#fff">Upgrade Your Plan</Typography>
            <Typography fontSize={12} color="rgba(255,255,255,0.8)">Unlock more agent slots</Typography>
          </Box>
        </Box>
        <DialogContent sx={{ p: 3 }}>
          <Typography fontSize={14} color="#374151">
            {limit === 0 ? "Your current plan doesn't support live agents." : `You've reached your agent limit (${limit}).`}
            {" "}Upgrade to add more team members.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 0, gap: 1 }}>
          <Button onClick={() => setUpgradeOpen(false)} fullWidth variant="outlined"
            sx={{ textTransform: "none", borderRadius: 2, borderColor: "#e5e7eb", color: "#6b7280" }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={() => { setUpgradeOpen(false); navigate("/app/plans"); }} fullWidth
            sx={{ textTransform: "none", borderRadius: 2, background: "linear-gradient(135deg, #f59e0b, #f97316)" }}>
            View Plans
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
